import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { 
  Database, 
  HardDrive, 
  FileJson, 
  Search,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Trash2,
  Edit,
  Eye,
  AlertTriangle,
  Loader2,
  X,
  Save,
  Copy
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";

const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const DatabaseManager = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [initializing, setInitializing] = useState(false);
  
  // Document viewer/editor state
  const [viewingDocument, setViewingDocument] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedJson, setEditedJson] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingDocument, setDeletingDocument] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);

  const initializeDatabase = async () => {
    try {
      setInitializing(true);
      setLoadError(false);
      const response = await api.post("/admin/database/initialize");
      toast.success("Database initialized successfully");
      console.log("Init results:", response.data.results);
      fetchStats();
    } catch (error) {
      console.error("Error initializing database:", error);
      toast.error("Failed to initialize database: " + (error.response?.data?.detail || error.message));
    } finally {
      setInitializing(false);
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const response = await api.get("/admin/database/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching database stats:", error);
      setLoadError(true);
      toast.error("Failed to load database statistics");
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async (collectionName, pageNum = 1, search = "") => {
    try {
      setDocumentsLoading(true);
      const params = new URLSearchParams({
        page: pageNum,
        limit: 20
      });
      if (search) params.append("search", search);
      
      const response = await api.get(`/admin/database/collections/${collectionName}/documents?${params}`);
      setDocuments(response.data.documents);
      setTotalPages(response.data.totalPages);
      setTotalDocs(response.data.total);
      setPage(pageNum);
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast.error("Failed to load documents");
    } finally {
      setDocumentsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleCollectionClick = (collectionName) => {
    setSelectedCollection(collectionName);
    setSearchQuery("");
    setPage(1);
    fetchDocuments(collectionName, 1, "");
  };

  const handleSearch = () => {
    if (selectedCollection) {
      fetchDocuments(selectedCollection, 1, searchQuery);
    }
  };

  const handleViewDocument = (doc) => {
    setViewingDocument(doc);
    setEditedJson(JSON.stringify(doc, null, 2));
    setEditMode(false);
  };

  const handleSaveDocument = async () => {
    try {
      setSaving(true);
      const parsedDoc = JSON.parse(editedJson);
      const docId = viewingDocument.id || viewingDocument._id;
      
      await api.put(`/admin/database/collections/${selectedCollection}/documents/${docId}`, {
        document: parsedDoc
      });
      
      toast.success("Document updated successfully");
      setViewingDocument(null);
      fetchDocuments(selectedCollection, page, searchQuery);
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error("Invalid JSON format");
      } else {
        console.error("Error saving document:", error);
        toast.error("Failed to save document");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (doc) => {
    setDeletingDocument(doc);
    setDeleteConfirmation("");
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    const docId = deletingDocument.id || deletingDocument._id;
    
    if (deleteConfirmation !== docId) {
      toast.error("Confirmation does not match");
      return;
    }
    
    try {
      setDeleting(true);
      await api.delete(`/admin/database/collections/${selectedCollection}/documents/${docId}`, {
        data: { confirmation: deleteConfirmation }
      });
      
      toast.success("Document deleted successfully");
      setDeleteDialogOpen(false);
      setDeletingDocument(null);
      fetchDocuments(selectedCollection, page, searchQuery);
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error(error.response?.data?.detail || "Failed to delete document");
    } finally {
      setDeleting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Show initialize button if database is empty OR if there was an error loading
  if (loadError || stats?.collections === 0 || stats?.totalDocuments === 0) {
    return (
      <div className="space-y-6">
        <Card className="border-dashed">
          <CardContent className="p-12 text-center">
            <Database className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-xl font-semibold mb-2">
              {loadError ? "Database Connection Issue" : "Database is Empty"}
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              {loadError 
                ? "Could not load database statistics. This may be a new database that needs initialization, or there may be a connection issue. Try initializing the database with essential platform data."
                : "The database has no collections. Initialize it with essential platform data including subscription packages, feature flags, and default settings."
              }
            </p>
            <div className="flex flex-col gap-3 items-center">
              <Button onClick={initializeDatabase} disabled={initializing} size="lg">
                {initializing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Initializing...
                  </>
                ) : (
                  <>
                    <Database className="w-4 h-4 mr-2" />
                    Initialize Database
                  </>
                )}
              </Button>
              {loadError && (
                <Button variant="outline" onClick={fetchStats} size="sm">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry Connection
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Database Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Database className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Database</p>
                <p className="font-semibold">{stats?.database}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <FileJson className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Collections</p>
                <p className="font-semibold">{stats?.collections}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <FileJson className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Documents</p>
                <p className="font-semibold">{stats?.totalDocuments?.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/10 rounded-lg">
                <HardDrive className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Data Size</p>
                <p className="font-semibold">{formatBytes(stats?.dataSize || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Collections List */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Collections</CardTitle>
              <Button variant="ghost" size="icon" onClick={fetchStats}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[500px] overflow-y-auto">
              {stats?.collectionStats?.map((coll) => (
                <button
                  key={coll.name}
                  onClick={() => handleCollectionClick(coll.name)}
                  className={cn(
                    "w-full px-4 py-3 text-left hover:bg-muted/50 border-b transition-colors",
                    selectedCollection === coll.name && "bg-muted"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium truncate">{coll.name}</span>
                    <span className="text-sm text-muted-foreground">{coll.count}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {formatBytes(coll.size)}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Documents View */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="text-lg">
                  {selectedCollection ? `${selectedCollection}` : "Select a collection"}
                </CardTitle>
                {selectedCollection && (
                  <CardDescription>{totalDocs} documents</CardDescription>
                )}
              </div>
              
              {selectedCollection && (
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Search by id, name, email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    className="w-64"
                  />
                  <Button variant="outline" size="icon" onClick={handleSearch}>
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!selectedCollection ? (
              <div className="text-center py-12 text-muted-foreground">
                <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Select a collection to view documents</p>
              </div>
            ) : documentsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileJson className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No documents found</p>
              </div>
            ) : (
              <>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[300px]">ID</TableHead>
                        <TableHead>Preview</TableHead>
                        <TableHead className="w-[120px] text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {documents.map((doc, idx) => {
                        const docId = doc.id || doc._id;
                        // Create a preview of the document
                        const preview = Object.entries(doc)
                          .filter(([key]) => !["_id", "id", "password_hash", "invite_token"].includes(key))
                          .slice(0, 3)
                          .map(([key, value]) => {
                            const strValue = typeof value === "object" ? JSON.stringify(value) : String(value);
                            return `${key}: ${strValue.substring(0, 30)}${strValue.length > 30 ? "..." : ""}`;
                          })
                          .join(" | ");
                        
                        return (
                          <TableRow key={docId || idx}>
                            <TableCell className="font-mono text-xs">
                              <div className="flex items-center gap-2">
                                <span className="truncate max-w-[250px]">{docId}</span>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-6 w-6"
                                  onClick={() => copyToClipboard(docId)}
                                >
                                  <Copy className="w-3 h-3" />
                                </Button>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground truncate max-w-[400px]">
                              {preview}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => handleViewDocument(doc)}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => {
                                    handleViewDocument(doc);
                                    setEditMode(true);
                                  }}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  className="text-red-500 hover:text-red-600"
                                  onClick={() => handleDeleteClick(doc)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">
                      Page {page} of {totalPages}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page <= 1}
                        onClick={() => fetchDocuments(selectedCollection, page - 1, searchQuery)}
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= totalPages}
                        onClick={() => fetchDocuments(selectedCollection, page + 1, searchQuery)}
                      >
                        Next
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Document Viewer/Editor Dialog */}
      <Dialog open={!!viewingDocument} onOpenChange={(open) => !open && setViewingDocument(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editMode ? (
                <>
                  <Edit className="w-5 h-5" />
                  Edit Document
                </>
              ) : (
                <>
                  <Eye className="w-5 h-5" />
                  View Document
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {viewingDocument?.id || viewingDocument?._id}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden">
            {editMode ? (
              <textarea
                value={editedJson}
                onChange={(e) => setEditedJson(e.target.value)}
                className="w-full h-[400px] p-4 font-mono text-sm bg-muted rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                spellCheck={false}
              />
            ) : (
              <pre className="w-full h-[400px] p-4 font-mono text-sm bg-muted rounded-lg overflow-auto">
                {JSON.stringify(viewingDocument, null, 2)}
              </pre>
            )}
          </div>
          
          <DialogFooter className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {!editMode && (
                <Button variant="outline" onClick={() => setEditMode(true)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              )}
              <Button 
                variant="outline"
                onClick={() => copyToClipboard(editMode ? editedJson : JSON.stringify(viewingDocument, null, 2))}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setViewingDocument(null)}>
                Close
              </Button>
              {editMode && (
                <Button onClick={handleSaveDocument} disabled={saving}>
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Document
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                This action cannot be undone. This will permanently delete the document from the <strong>{selectedCollection}</strong> collection.
              </p>
              <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg p-4">
                <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
                  To confirm, type the document ID:
                </p>
                <code className="text-xs bg-red-100 dark:bg-red-900/50 px-2 py-1 rounded block mb-3 break-all">
                  {deletingDocument?.id || deletingDocument?._id}
                </code>
                <Input
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder="Type document ID to confirm"
                  className="font-mono text-sm"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleting || deleteConfirmation !== (deletingDocument?.id || deletingDocument?._id)}
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete Document
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DatabaseManager;
